import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { isMuted, toggleMute, playChime } from "../lib/chime";
import { Volume2, VolumeX, Menu, X } from "lucide-react";
import { Enso } from "./SacredGeometry";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [muted, setMuted] = useState(isMuted());
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const active = user && user.subscription?.status === "active";

  const links = [
    { to: "/courses", label: "The Path" },
    { to: "/membership", label: "Membership" },
  ];
  if (user) links.push({ to: "/dashboard", label: "My Altar" });
  if (user?.role === "admin") links.push({ to: "/admin", label: "Guardian" });

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-sanctuary-deep/85 backdrop-blur-xl border-b border-gold/15" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Enso className="w-9 h-9 text-gold transition-transform duration-700 group-hover:rotate-45" />
          <div className="leading-none">
            <div className="font-display text-xl text-parchment">Reiki Sanctuary</div>
            <div className="font-jp text-[0.6rem] tracking-[0.3em] text-gold/70">霊気 の 道</div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`link-underline text-sm tracking-wide transition-colors duration-300 ${
                location.pathname === l.to ? "text-gold" : "text-text-muted hover:text-parchment"
              }`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const m = toggleMute();
              setMuted(m);
              if (!m) playChime();
            }}
            className="w-9 h-9 grid place-items-center rounded-full gold-border text-gold/80 hover:text-gold hover:bg-gold/10 transition-colors"
            title={muted ? "Sound off" : "Sound on"}
            data-testid="nav-sound-toggle"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {active && (
                <span className="text-[0.65rem] uppercase tracking-widest px-3 py-1 rounded-full bg-sage/15 text-sage-light border border-sage/30" data-testid="nav-subscription-pill">
                  Member
                </span>
              )}
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm text-text-muted hover:text-parchment transition-colors"
                data-testid="nav-logout-button"
              >
                Depart
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden md:inline-flex text-sm px-5 py-2 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors"
              data-testid="nav-enter-button"
            >
              Enter
            </Link>
          )}

          <button className="md:hidden text-gold" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-sanctuary-surface border-t border-gold/15 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-parchment/90 text-lg font-display" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          {user ? (
            <button onClick={async () => { await logout(); navigate("/"); }} className="text-left text-text-muted" data-testid="mobile-logout">Depart</button>
          ) : (
            <Link to="/login" className="text-gold" data-testid="mobile-enter">Enter the Sanctuary</Link>
          )}
        </div>
      )}
    </header>
  );
};
